"use client";
import { ReactNode, createContext, useContext, useState } from "react";
import { View } from "react-native";
import {
	SharedValue,
	measure,
	runOnJS,
	useAnimatedReaction,
	useAnimatedRef,
	useDerivedValue,
} from "react-native-reanimated";
import { DragArea } from "src/providers/DragSelectionProvider/DragSelectionProvider";

export type DragSelectionContext = {
	dragArea: Readonly<SharedValue<DragArea>>;
	enabled: boolean;
	isDragging: boolean;
};

const DragSelectionContext = createContext<DragSelectionContext | null>(null);

export const useDragSelectionContext = () => {
	const context = useContext(DragSelectionContext);

	if (!context) {
		throw new Error("useDragSelectionContext must be used within a DragSelectionProvider");
	}
	return context;
};

export const DragSelectionWrapper = ({
	children,
	dragArea,
	enabled,
}: {
	children: ReactNode;
	dragArea: Readonly<SharedValue<DragArea>>;
	enabled: boolean;
}) => {
	const [isDragging, setIsDragging] = useState(() => dragArea.get() !== null);
	useAnimatedReaction(
		() => {
			return dragArea.value !== null;
		},
		(currentValue, previousValue) => {
			"worklet";
			if (currentValue !== previousValue) {
				runOnJS(setIsDragging)(currentValue);
			}
		},
		[dragArea],
	);

	return (
		<>
			<DragSelectionContext.Provider
				value={{
					dragArea,
					enabled,
					isDragging,
				}}
			>
				{children}
			</DragSelectionContext.Provider>
		</>
	);
};

export interface SelectableElement {
	selected: boolean;
}

export const useDragSelection = () => {
	const elementRef = useAnimatedRef<View>();
	const { dragArea, enabled, isDragging } = useDragSelectionContext();

	const [isSelected, setIsSelected] = useState(false);

	const selected = useDerivedValue(() => {
		if (!dragArea.value) return false;

		const elementPosition = measure(elementRef);
		if (!elementPosition) return false;

		// Get element position
		const elementLeft = elementPosition.pageX;
		const elementRight = elementPosition.pageX + elementPosition.width;
		const elementTop = elementPosition.pageY;
		const elementBottom = elementPosition.pageY + elementPosition.height;

		// Get drag area position
		const dragAreaLeft = dragArea.value.left;
		const dragAreaRight = dragArea.value.left + dragArea.value.width;
		const dragAreaTop = dragArea.value.top;
		const dragAreaBottom = dragArea.value.top + dragArea.value.height;

		// Check for overlap
		const isAbove = dragAreaBottom < elementTop;
		const isBelow = dragAreaTop > elementBottom;
		const isLeft = dragAreaRight < elementLeft;
		const isRight = dragAreaLeft > elementRight;

		const selected = !(isAbove || isBelow || isLeft || isRight);

		return selected;
	}, [dragArea, elementRef]);

	useAnimatedReaction(
		() => {
			return selected.value;
		},
		(currentValue, previousValue) => {
			"worklet";
			if (currentValue !== previousValue) {
				runOnJS(setIsSelected)(currentValue);
			}
		},
		[selected],
	);

	return {
		elementRef,
		isSelected,
		isDragging,
		enabled,
	};
};
